import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { SystemDailyPerformance, SummaryMetrics } from '../../types/system';
import LoadingOverlay from '../LoadingOverlay';
import StakingCalculator from './StakingCalculator';
import SystemPerformance from './SystemPerformance';
import SystemTodaysRaces from './SystemTodaysRaces';

export default function SystemDashboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryMetrics, setSummaryMetrics] = useState<SummaryMetrics>({
    status: 'In Progress',
    betsPlaced: '0',
    totalPL: 0,
  });
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    if (user) {
      fetchDailyPerformance();
    }
  }, [user]);

  const fetchDailyPerformance = async () => {
    try {
      setLoading(true);
      setError(null);

      const today = new Date().toISOString().split('T')[0];
      
      // Try to get today's record
      const { data, error: fetchError } = await supabase
        .from('system_daily_performance')
        .select('*')
        .eq('user_id', user!.id)
        .eq('date', today)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        throw fetchError;
      }

      if (!data) {
        // Create a new record for today
        const { data: newRecord, error: insertError } = await supabase
          .from('system_daily_performance')
          .insert([
            {
              user_id: user!.id,
              date: today,
              bets_placed: 0,
              total_pl: 0,
              status: 'In Progress'
            }
          ])
          .select()
          .single();

        if (insertError) throw insertError;
        updateSummaryMetrics(newRecord);
      } else {
        updateSummaryMetrics(data);
      }
    } catch (err) {
      console.error('Error fetching daily performance:', err);
      setError('Failed to load daily performance data');
    } finally {
      setLoading(false);
    }
  };

  const updateSummaryMetrics = (data: SystemDailyPerformance) => {
    setSummaryMetrics({
      status: data.status,
      betsPlaced: data.bets_placed.toString(),
      totalPL: data.total_pl,
    });
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Welcome Guide */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 shadow-lg rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 cursor-pointer select-none" onClick={() => setShowWelcome((v) => !v)}>
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-white">
              Welcome to Horse Racing Oracle
            </h2>
          </div>
          <button
            className="text-white hover:text-primary-200 focus:outline-none"
            aria-label={showWelcome ? 'Hide guide' : 'Show guide'}
            onClick={e => { e.stopPropagation(); setShowWelcome((v) => !v); }}
          >
            <svg className={`h-6 w-6 transform transition-transform duration-200 ${showWelcome ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        {showWelcome && (
          <>
            <div className="px-6 py-4 bg-white bg-opacity-10">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center">
                    <span className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-white text-primary-600 font-semibold">
                      1
                    </span>
                    <h3 className="ml-3 text-lg font-medium text-white">Find Races</h3>
                  </div>
                  <p className="mt-2 text-sm text-primary-100">
                    Check for qualifying races in the Today's Races section below. Look for races that meet our selection criteria.
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center">
                    <span className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-white text-primary-600 font-semibold">
                      2
                    </span>
                    <h3 className="ml-3 text-lg font-medium text-white">Calculate Stake</h3>
                  </div>
                  <p className="mt-2 text-sm text-primary-100">
                    Use the staking calculator to determine your optimal stake based on your daily target and previous results.
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center">
                    <span className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-white text-primary-600 font-semibold">
                      3
                    </span>
                    <h3 className="ml-3 text-lg font-medium text-white">Place Bet</h3>
                  </div>
                  <p className="mt-2 text-sm text-primary-100">
                    Place your bet with your chosen bookmaker using the calculated stake amount.
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center">
                    <span className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-white text-primary-600 font-semibold">
                      4
                    </span>
                    <h3 className="ml-3 text-lg font-medium text-white">Record Result</h3>
                  </div>
                  <p className="mt-2 text-sm text-primary-100">
                    Record your bet and its outcome using the 'Record Bet' feature to track your progress.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-white">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-900">Our System Rules</h3>
                  <div className="mt-2 text-sm text-gray-600">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>If you win, you've achieved your daily profit target - stop betting for the day</li>
                      <li>If you lose, look for the next qualifying race and recalculate your stake</li>
                      <li>Only bet on qualifying races</li>
                      <li>For accurate odds and filtering, check if a race qualifies 1 to 5 minutes before the race starts</li>
                      <li>If you have 8 losing bets in a row, stop betting for the day</li>
                      <li>Always use the staking calculator to determine your next bet size</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-center">
                <a
                  href="/dashboard/system/user-guide"
                  className="inline-flex items-center px-6 py-3 rounded-md text-base font-semibold bg-primary-600 text-white shadow hover:bg-primary-700 transition"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Full User Guide
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Today's Performance */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Today's Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500">Status</h3>
            <p className={`mt-2 text-xl font-semibold ${
              summaryMetrics.status === 'Achieved' ? 'text-green-600' :
              summaryMetrics.status === 'Not Achieved' ? 'text-red-600' :
              'text-gray-900'
            }`}>
              {summaryMetrics.status}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500">Bets Placed</h3>
            <p className="mt-2 text-xl font-semibold text-gray-900">
              {summaryMetrics.betsPlaced}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500">Profit/Loss</h3>
            <p className={`mt-2 text-xl font-semibold ${
              summaryMetrics.totalPL > 0 ? 'text-green-600' :
              summaryMetrics.totalPL < 0 ? 'text-red-600' :
              'text-gray-900'
            }`}>
              £{summaryMetrics.totalPL.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Today's Races */}
      <SystemTodaysRaces />

      {/* Staking Calculator */}
      <StakingCalculator />

      {/* Recent Performance */}
      <SystemPerformance />
    </div>
  );
} 